import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { useMe } from '@/lib/useMe'
import { useUnreadAlertCount } from '@/lib/useAlerts'
import { useAuthStore } from '@/stores/auth'

const ROUTE_LABELS: Record<string, string> = {
  '/dashboard': 'Dashboard',
  '/positions': 'Portfolio',
  '/trades': 'Trades',
  '/rules': 'Rules',
  '/alerts': 'Alerts',
  '/settings': 'Settings',
}

export function Header() {
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const clearAccessToken = useAuthStore((s) => s.clearAccessToken)
  const { data: user, isLoading } = useMe()
  const { data: unreadCount = 0 } = useUnreadAlertCount()

  const [open, setOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onPointer = (e: MouseEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onPointer)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const handleLogout = () => {
    setOpen(false)
    clearAccessToken()
    queryClient.clear()
    navigate('/login', { replace: true })
  }

  const displayName = isLoading ? '…' : (user?.username ?? 'Unbekannt')
  const routeLabel = ROUTE_LABELS[location.pathname] ?? '—'

  return (
    <header className="hairline-b flex h-12 shrink-0 items-center justify-between bg-panel px-6">
      <div className="flex items-baseline gap-3">
        <span className="tracked-caps text-sm text-ink">Sextant</span>
        <span className="font-mono text-xs text-faint">·</span>
        <span className="font-mono text-xs text-muted">{routeLabel}</span>
      </div>

      <div className="flex items-center gap-5">
        <button
          type="button"
          onClick={() => navigate('/alerts')}
          aria-label={
            unreadCount > 0 ? `${unreadCount} ungelesene Alerts` : 'Alerts (keine ungelesenen)'
          }
          className="relative flex items-baseline gap-2 text-muted transition-colors hover:text-bernstein"
        >
          <span className="font-mono text-xs">Alerts</span>
          {unreadCount > 0 && (
            <span
              aria-hidden="true"
              className="tracked-caps min-w-[1.25rem] rounded-full bg-bernstein px-1.5 py-0.5 text-center font-mono text-[10px] text-void"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </button>

        <div ref={menuRef} className="relative">
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            aria-haspopup="menu"
            aria-expanded={open}
            className="flex items-baseline gap-2 text-ink transition-colors hover:text-bernstein"
          >
            <span className="text-sm">{displayName}</span>
            <span
              aria-hidden="true"
              className={`text-[9px] text-muted transition-transform duration-200 ${
                open ? 'rotate-180' : ''
              }`}
            >
              ▾
            </span>
          </button>

          {open && (
            <div
              role="menu"
              className="dropdown-enter absolute right-0 mt-3 w-56 border border-hairline bg-panel"
            >
              <div className="px-5 pt-4 pb-3">
                <p className="text-base font-medium text-ink">{user?.username ?? '—'}</p>
                <p className="mt-1.5 font-mono text-[10px] text-muted">
                  {user?.timezone ?? 'Zeitzone unbekannt'}
                </p>
              </div>
              <div className="hairline-t">
                <button
                  role="menuitem"
                  type="button"
                  onClick={handleLogout}
                  className="tracked-caps block w-full px-5 py-3 text-left text-[11px] text-muted transition-colors hover:bg-void hover:text-bernstein"
                >
                  Abmelden
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  )
}
